/**
 * SHARED CONSTANTS AND CONFIGURATION
 * Shared between web app and workers
 */

export enum IndexSymbol {
    NIFTY = 'NIFTY',
    BANKNIFTY = 'BANKNIFTY',
    SENSEX = 'SENSEX',
}

export enum OptionType {
    CE = 'CE',
    PE = 'PE',
}

export enum AnalysisMode {
    OI = 'OI',
    VOLUME = 'VOLUME',
}

export enum TimeGap {
    M1 = '1m',
    M5 = '5m',
    M10 = '10m',
    M15 = '15m',
    M30 = '30m',
    H1 = '1h',
    H2 = '2h',
    H4 = '4h',
}

export enum UserRole
{
    ADMIN = 'admin',
    USER = 'user',
}

export enum UserStatus
{
    PENDING = 'pending',
    ACTIVE = 'approved',
    REJECTED = 'rejected',
}

// ============================================================================
// 1. SYMBOLS CONFIGURATION
// ============================================================================
export const SUPPORTED_SYMBOLS = [IndexSymbol.NIFTY, IndexSymbol.BANKNIFTY, IndexSymbol.SENSEX] as const;

export const SYMBOL_DISPLAY_NAMES: Record<IndexSymbol, string> = {
    [IndexSymbol.NIFTY]: 'NIFTY',
    [IndexSymbol.BANKNIFTY]: 'BANKNIFTY',
    [IndexSymbol.SENSEX]: 'SENSEX',
};

export const SYMBOL_METADATA: Record<IndexSymbol, { hasHistoricalData: boolean }> = {
    [IndexSymbol.NIFTY]: { hasHistoricalData: true },
    [IndexSymbol.BANKNIFTY]: { hasHistoricalData: true },
    [IndexSymbol.SENSEX]: { hasHistoricalData: true },
};

export const SYMBOL_VARIANTS: Record<string, string[]> = {
    [IndexSymbol.NIFTY]: ['NIFTY 50', 'NIFTY50', 'NSE_INDEX|Nifty 50', 'NSE_INDEX|NIFTY'],
    [IndexSymbol.BANKNIFTY]: ['NIFTY BANK', 'NIFTYBANK', 'NSE_INDEX|Nifty Bank', 'NSE_INDEX|BANKNIFTY', 'BANK NIFTY'],
    [IndexSymbol.SENSEX]: ['BSE_INDEX|SENSEX', 'BSESENSEX'],
};

// ============================================================================
// 2. API CONFIGURATION
// ============================================================================

export const UPSTOX_API_CONFIG = {
    REAL_URL: 'https://service.upstox.com/option-analytics-tool/open/v1/strategy-chains',
    CANDLE_BASE_URL: 'https://api.upstox.com/v3/historical-candle',
    symbols: {
        [IndexSymbol.NIFTY]: {
            assetKey: 'NSE_INDEX|Nifty 50',
            assetKeyEncoded: 'NSE_INDEX%7CNifty+50',
            displayName: 'NIFTY',
        },
        [IndexSymbol.BANKNIFTY]: {
            assetKey: 'NSE_INDEX|Nifty Bank',
            assetKeyEncoded: 'NSE_INDEX%7CNifty+Bank',
            displayName: 'BANKNIFTY',
        },
        [IndexSymbol.SENSEX]: {
            assetKey: 'BSE_INDEX|SENSEX',
            assetKeyEncoded: 'BSE_INDEX%7CSENSEX',
            displayName: 'SENSEX',
        },
    }
};

export const UPSTOX_INSTRUMENT_KEYS: Record<string, string> = {
    NIFTY: 'NSE_INDEX|Nifty 50',
    BANKNIFTY: 'NSE_INDEX|Nifty Bank',
    SENSEX: 'BSE_INDEX|SENSEX',
    'INDIA VIX': 'NSE_INDEX|India VIX',
    INDIA_VIX: 'NSE_INDEX|India VIX',
};

export const HF_CONFIG = {
    REPO: 'bathinimallikarjuna637/Options-Oracle',
    BASE_API_URL: 'https://huggingface.co/api/datasets/bathinimallikarjuna637/Options-Oracle/tree/main',
    BASE_RESOLVE_URL: 'https://huggingface.co/datasets/bathinimallikarjuna637/Options-Oracle/resolve/main',
    TOKEN: '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',
};

export const API_CONFIG = {
    SOURCES: {
        EXPIRY_CSV: 'https://docs.google.com/spreadsheets/d/e/2PACX-1vQpDYt-WXR1FfLdVbWUmgjbgsFJYlJZvTiXBvBmXqe0JZuQ65f2hLiUtrmN4MLm5xycG9aC5oQ2kHFO/pub?gid=0&single=true&output=csv',
        INTRADAY_SHEET_LINKS: {
            [IndexSymbol.NIFTY]: 'https://docs.google.com/spreadsheets/d/e/2PACX-1vSwBaUIM16XND5CdYgmpbwf9OHlJeaV7dS93BqSgQTHPLNQU9ArgvKC6fL8_avGeF2qH9ahEtmBYRX7/pub?gid=1903702304&single=true&output=csv',
            [IndexSymbol.BANKNIFTY]: 'https://docs.google.com/spreadsheets/d/e/2PACX-1vS6C5QEfSskrjXRRfqJ9A1mmk2PDQ4Sa2vt8bfmqs7wtbh-1vhbuZrCwbSaSBD0idM6S8aP_uP4V16G/pub?gid=1903702304&single=true&output=csv',
            [IndexSymbol.SENSEX]: 'https://docs.google.com/spreadsheets/d/e/2PACX-1vRPG1FFd07Tzo3aApcHAXyrhdRRnQnrZXYO1tR_9p7aa1fpAvokkzKuUBvNxfKM5cz90PbjL-T_pBF4/pub?gid=1903702304&single=true&output=csv',
        },
        LIVE_FALLBACK_SHEET_LINKS: {
            [IndexSymbol.NIFTY]: 'https://docs.google.com/spreadsheets/d/e/2PACX-1vSwBaUIM16XND5CdYgmpbwf9OHlJeaV7dS93BqSgQTHPLNQU9ArgvKC6fL8_avGeF2qH9ahEtmBYRX7/pub?gid=395551059&single=true&output=csv',
            [IndexSymbol.BANKNIFTY]: 'https://docs.google.com/spreadsheets/d/e/2PACX-1vS6C5QEfSskrjXRRfqJ9A1mmk2PDQ4Sa2vt8bfmqs7wtbh-1vhbuZrCwbSaSBD0idM6S8aP_uP4V16G/pub?gid=1903702304&single=true&output=csv',
            [IndexSymbol.SENSEX]: 'https://docs.google.com/spreadsheets/d/e/2PACX-1vS6C5QEfSskrjXRRfqJ9A1mmk2PDQ4Sa2vt8bfmqs7wtbh-1vhbuZrCwbSaSBD0idM6S8aP_uP4V16G/pub?gid=395551059&single=true&output=csv',
        },
        FALLBACK_GROWW_CSV: 'https://growwapi-assets.groww.in/instruments/instrument.csv',
        HUGGING_FACE_REPO: 'bathinimallikarjuna637/Options-Oracle',
        HUGGING_FACE_TOKEN: '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',
    },
    CSV_CONFIG: {
        NUMERIC_FIELDS: [
            'strikePrice',
            'callPrice',
            'callChangePercent',
            'callOpenInterest',
            'callVolume',
            'callImpliedVolatility',
            'callChange',
            'callOpenInterestChange',
            'putPrice',
            'putChangePercent',
            'putOpenInterest',
            'putVolume',
            'putImpliedVolatility',
            'putChange',
            'putOpenInterestChange',
            'Index_lastPrice',
            'VIX_value',
        ],
        HEADER_MAPPINGS: {
            strike: 'strikePrice',
            symbol: 'underlyingName',
            expiry: 'expirationDate',
            call_change_p: 'callChangePercent',
            call_oi_chg: 'callOpenInterestChange',
            put_change_p: 'putChangePercent',
            put_oi_chg: 'putOpenInterestChange',
        },
    },
};

// ============================================================================
// 3. STRIKE GAP
// ============================================================================
export const STRIKE_STEP: Record<IndexSymbol, number> = {
    [IndexSymbol.NIFTY]: 50,
    [IndexSymbol.BANKNIFTY]: 100,
    [IndexSymbol.SENSEX]: 100,
};

export function getCanonicalSymbol ( symbol: string ): IndexSymbol | string {
    if (!symbol) return ''
    const s = String(symbol).replace(/[^A-Z0-9]/gi, '').toUpperCase()
    if ( s.includes('BANK') ) return IndexSymbol.BANKNIFTY
    if ( s.includes('SENSEX') ) return IndexSymbol.SENSEX
    if ( s.includes('NIFTY') ) return IndexSymbol.NIFTY
    return symbol
}

export function getStrikeStepForSymbol ( symbol: IndexSymbol | string ): number {
    const canonical = getCanonicalSymbol( typeof symbol === 'string' ? symbol : symbol )
    return STRIKE_STEP[ canonical as IndexSymbol ] || 50
}

export function normalizeSymbolLookupKey ( value: string ): string {
    return String( value || '' )
        .replace( /[^A-Za-z0-9]/g, '' )
        .toUpperCase()
}

export function getSymbolLookupCandidates ( symbol: string ): string[] {
    const canonical = String( getCanonicalSymbol( symbol ) )
    const symbolConfig = ( UPSTOX_API_CONFIG.symbols as Record<string, { assetKey: string; displayName: string }> )[
        canonical
    ]

    const aliases = new Set<string>( [
        symbol,
        canonical,
        ...( SYMBOL_VARIANTS[ canonical ] || [] ),
        ...( symbolConfig ? [ symbolConfig.assetKey, symbolConfig.displayName ] : [] ),
        ...( UPSTOX_INSTRUMENT_KEYS[ canonical ] ? [ UPSTOX_INSTRUMENT_KEYS[ canonical ] ] : [] )
    ] )
    const aliasesList: string[] = []
    for ( const a of aliases as any )
    {
        aliasesList.push( a )
    }
    return aliasesList
}

export function isSymbolLookupMatch ( candidateKey: string, symbol: string ): boolean {
    const normalizedCandidate = normalizeSymbolLookupKey( candidateKey )
    if ( !normalizedCandidate ) return false

    const possibleKeys = getSymbolLookupCandidates( symbol )
    let possibleList: string[] = []
    for (const p of possibleKeys as any) possibleList.push(p)

    for ( const k of possibleList )
    {
        const normalized = normalizeSymbolLookupKey( k )
        if ( normalized && normalizedCandidate.includes( normalized ) ) return true
    }

    return false
}

// ============================================================================
// 4. COLORS
// ============================================================================
export const COLORS = {
    SUPPORT: '#10b981',
    RESISTANCE: '#ef4444',
    NEUTRAL: '#6b7280',
    TEXT: { POSITIVE: '#059669', NEGATIVE: '#dc2626', DEFAULT: '#111827' },
};

// ============================================================================
// 5. MARKET HOURS (Shared Logic)
// ============================================================================
export interface MarketHourItem {
    hour: number;
    label: string;
    session: string;
    icon: string;
}

export interface MarketMilestone {
    label: string;
    min: number;
    badge: string;
}

// Master configurable session hours — Changing these values will update all calculations, pickers, slicers, and stores across the codebase
const MARKET_OPEN_HOUR = 9;
const MARKET_OPEN_MINUTE = 10;
const MARKET_CLOSE_HOUR = 16;
const MARKET_CLOSE_MINUTE = 0;
const MARKET_BASE_HOUR = 9;
const MARKET_BASE_MINUTE = 16;

const MARKET_START_MINUTES = MARKET_OPEN_HOUR * 60 + MARKET_OPEN_MINUTE; // 550
const MARKET_END_MINUTES = MARKET_CLOSE_HOUR * 60 + MARKET_CLOSE_MINUTE; // 960
const MARKET_TOTAL_MINUTES = MARKET_END_MINUTES - MARKET_START_MINUTES + 1; // 411
const MARKET_BASE_MINUTES = MARKET_BASE_HOUR * 60 + MARKET_BASE_MINUTE; // 556

const MARKET_START_TIME_STR = `${String(MARKET_OPEN_HOUR).padStart(2, '0')}:${String(MARKET_OPEN_MINUTE).padStart(2, '0')}`; // '09:10'
const MARKET_END_TIME_STR = `${String(MARKET_CLOSE_HOUR).padStart(2, '0')}:${String(MARKET_CLOSE_MINUTE).padStart(2, '0')}`; // '16:00'
const MARKET_BASE_TIME_STR = `${String(MARKET_BASE_HOUR).padStart(2, '0')}:${String(MARKET_BASE_MINUTE).padStart(2, '0')}`; // '09:16'

export const MARKET_HOURS = {
    // Configurable master anchors
    OPEN_HOUR: MARKET_OPEN_HOUR,
    OPEN_MINUTE: MARKET_OPEN_MINUTE,
    CLOSE_HOUR: MARKET_CLOSE_HOUR,
    CLOSE_MINUTE: MARKET_CLOSE_MINUTE,
    TIMEZONE: 'Asia/Kolkata',
    TRADING_DAYS: [ 1, 2, 3, 4, 5 ],

    // Derived session bounds
    START_HOUR: MARKET_OPEN_HOUR,
    START_MINUTE: MARKET_OPEN_MINUTE,
    END_HOUR: MARKET_CLOSE_HOUR,
    END_MINUTE: MARKET_CLOSE_MINUTE,
    START_MINUTES: MARKET_START_MINUTES,
    END_MINUTES: MARKET_END_MINUTES,
    TOTAL_MINUTES: MARKET_TOTAL_MINUTES,
    START_TIME_STR: MARKET_START_TIME_STR,
    END_TIME_STR: MARKET_END_TIME_STR,

    // Base Time
    BASE_HOUR: MARKET_BASE_HOUR,
    BASE_MINUTE: MARKET_BASE_MINUTE,
    BASE_MINUTES: MARKET_BASE_MINUTES,
    BASE_TIME_STR: MARKET_BASE_TIME_STR,

    // Playback fallback sequence
    FALLBACK_MINUTES: Array.from({ length: MARKET_TOTAL_MINUTES }, (_, i) => MARKET_START_MINUTES + i),

    // Hours list with session descriptions
    HOURS_LIST: [
        { hour: 9, label: '09 AM', session: 'Open', icon: '🌅' },
        { hour: 10, label: '10 AM', session: 'Morning', icon: '⚡' },
        { hour: 11, label: '11 AM', session: 'Mid-AM', icon: '📈' },
        { hour: 12, label: '12 PM', session: 'Noon', icon: '☀️' },
        { hour: 13, label: '01 PM', session: 'Post-Noon', icon: '🥪' },
        { hour: 14, label: '02 PM', session: 'EU Session', icon: '🌍' },
        { hour: 15, label: '03 PM', session: 'Power Hr', icon: '🎯' },
        { hour: 16, label: '04 PM', session: 'Close', icon: '🏁' },
    ] as MarketHourItem[],

    // Milestone quick-jump markers
    KEY_MILESTONES: [
        { label: `${MARKET_START_TIME_STR} Open`, min: MARKET_START_MINUTES, badge: `🔔 ${MARKET_START_TIME_STR} Open` },
        { label: '09:15', min: 555, badge: '⚡ 09:15' },
        { label: '10:30', min: 630, badge: '⚡ 10:30 Morning' },
        { label: '12:30', min: 750, badge: '☀️ 12:30 Midday' },
        { label: '14:00', min: 840, badge: '🌍 14:00 EU Open' },
        { label: '15:00', min: 900, badge: '🎯 15:00 Power Hr' },
        { label: '15:30', min: 930, badge: '🔔 15:30 EOD' },
        { label: `${MARKET_END_TIME_STR} Close`, min: MARKET_END_MINUTES, badge: `🏁 ${MARKET_END_TIME_STR} Close` },
    ] as MarketMilestone[],
};

export const MARKET_HOURS_CONFIG = MARKET_HOURS;

// ============================================================================
// PLAYBACK SPEED CONFIGURATION (Centralized)
// ============================================================================
export const PLAYBACK_SPEEDS = [0.05, 0.1, 0.2, 0.25, 0.5, 0.75, 1, 1.5, 2, 3, 5, 10, 20, 50] as const;
export type PlaybackSpeed = typeof PLAYBACK_SPEEDS[number];
export const DEFAULT_PLAYBACK_SPEED: PlaybackSpeed = 1;

export const PLAYBACK_SPEED_LABELS: Record<number, string> = {
    0.05: '0.05x (5.0s • Slowest)',
    0.1:  '0.1x (3.0s • Deep Study)',
    0.2:  '0.2x (2.0s • Very Slow)',
    0.25: '0.25x (1.5s • Slow)',
    0.5:  '0.5x (1.0s • 1s Tick)',
    0.75: '0.75x (750ms)',
    1:    '1x (500ms • Normal)',
    1.5:  '1.5x (330ms)',
    2:    '2x (200ms • Fast)',
    3:    '3x (150ms)',
    5:    '5x (100ms)',
    10:   '10x (Turbo)',
    20:   '20x (Flash)',
    50:   '50x (Max Speed)',
};

export const PLAYBACK_SPEED_CONFIG: Record<number, { interval: number; stride: number }> = {
    0.05: { interval: 5000, stride: 1 }, // 5.0 seconds per step (ultra slow motion for deep analysis)
    0.1:  { interval: 3000, stride: 1 }, // 3.0 seconds per step (deep tape reading)
    0.2:  { interval: 2000, stride: 1 }, // 2.0 seconds per step (very slow)
    0.25: { interval: 1500, stride: 1 }, // 1.5 seconds per step (slow)
    0.5:  { interval: 1000, stride: 1 }, // 1.0 second per step (1s clock tick)
    0.75: { interval: 750,  stride: 1 }, // 750ms per step
    1:    { interval: 500,  stride: 1 }, // 500ms per step (standard smooth replay, 2 steps/sec)
    1.5:  { interval: 330,  stride: 1 }, // 330ms per step (3 steps/sec)
    2:    { interval: 200,  stride: 1 }, // 200ms per step (5 steps/sec)
    3:    { interval: 150,  stride: 1 }, // 150ms per step
    5:    { interval: 100,  stride: 2 }, // 100ms per step (stride 2)
    10:   { interval: 80,   stride: 5 }, // 80ms per step (stride 5)
    20:   { interval: 60,   stride: 10 },// 60ms per step (stride 10)
    50:   { interval: 50,   stride: 25 },// 50ms per step (stride 25)
};

export function isMarketDay(date?: Date): boolean {
    const d = date || new Date();
    // Use Intl for cross-platform TZ support if available, otherwise fallback
    try {
        const istDate = new Date(d.toLocaleString('en-US', { timeZone: MARKET_HOURS.TIMEZONE }));
        const day = istDate.getDay();
        return day !== 0 && day !== 6;
    } catch (e) {
        const day = d.getDay();
        return day !== 0 && day !== 6;
    }
}

export function isMarketHours (): boolean
{
    if ( !isMarketDay() ) return false
    const now = new Date()
    // Same approach for IST
    try {
        const istNow = new Date( now.toLocaleString( 'en-US', { timeZone: MARKET_HOURS.TIMEZONE } ) )
        const hours = istNow.getHours()
        const minutes = istNow.getMinutes()
        const timeInMinutes = hours * 60 + minutes
        return timeInMinutes >= MARKET_HOURS.START_MINUTES && timeInMinutes <= MARKET_HOURS.END_MINUTES
    } catch (e) {
        return false
    }
}

export function isAtMarketClose(time: string | number): boolean {
    if (typeof time === 'number') {
        return time >= 930 || time >= MARKET_HOURS.END_MINUTES;
    }
    const clean = String(time).trim();
    return clean === MARKET_HOURS.END_TIME_STR || clean === '15:30' || clean === '15:30:00' || clean === `${MARKET_HOURS.END_TIME_STR}:00`;
}

export function isFullDayRange(start: string | number, end: string | number): boolean {
    const startStr = typeof start === 'number'
        ? `${String(Math.floor(start / 60)).padStart(2, '0')}:${String(start % 60).padStart(2, '0')}`
        : String(start).trim().slice(0, 5);
    const endStr = typeof end === 'number'
        ? `${String(Math.floor(end / 60)).padStart(2, '0')}:${String(end % 60).padStart(2, '0')}`
        : String(end).trim().slice(0, 5);

    const isStartFull = startStr === MARKET_HOURS.START_TIME_STR || startStr === '09:15' || startStr === '09:16';
    const isEndFull = endStr === MARKET_HOURS.END_TIME_STR || endStr === '15:30';
    return isStartFull && isEndFull;
}

export const REFRESH_INTERVALS = {
    LIVE_DATA: 2000,
    BACKUP: 60000, // 1 minute — per user rules (backup every 1 min for all symbols/expiries)
    EXPIRY_UPDATE: 86400000,
}

// ============================================================================
// 6. STORAGE KEYS
// ============================================================================
export const STORAGE_KEYS = {
    USER_SETTINGS: 'oracle_user_settings',
    USER_STATE: 'oracle_user',
    STRIKE_HISTORY_ROWS: 'oracle_strike_history_rows',
    LIVE_STRIKE_ROWS: 'oracle_live_strike_rows',
    LIVE_STRIKE_COLUMNS: 'oracle_live_strike_columns',
    HOME_COLUMNS: 'oracle_home_columns',
    INSPECTOR_COLUMNS: 'oracle_inspector_columns',
    INSPECTOR_ROWS: 'oracle_inspector_rows',
    COOKIE_CONSENT: 'oracle_cookie_consent',
    DASHBOARD_ROWS: 'oracle_dashboard_rows',
    DASHBOARD_RANKING_ROWS: 'oracle_dashboard_ranking_rows',
    DASHBOARD_RANKING_COLUMNS: 'oracle_dashboard_ranking_columns',
    DASHBOARD_HISTORY_COLUMNS: 'oracle_dashboard_history_columns',
    VARIANCE_HISTORY_ROWS: 'oracle_variance_history_rows',
    VARIANCE_HISTORY_COLUMNS: 'oracle_variance_history_columns',
    CROSSOVER_HISTORY_ROWS: 'oracle_crossover_history_rows',
    CROSSOVER_HISTORY_COLUMNS: 'oracle_crossover_history_columns',
    CROSSOVER_COLUMNS: 'oracle_crossover_columns_v2',
    VARIANCE_COLUMNS: 'oracle_variance_columns_v2',
    RSI_TABLE_COLUMNS: 'oracle_rsi_table_columns',
    RSI_TABLE_ROWS: 'oracle_rsi_table_rows',
    STRIKES_LADDER_DENSITY: 'oracle_strikes_ladder_density',
    STRIKES_LADDER_FLOW_TIER_MODE: 'oracle_strikes_ladder_flow_tier_mode',
    STRIKES_LADDER_FLOW_PINNED: 'oracle_strikes_ladder_flow_pinned',
    INDEX_CONSTITUENTS_PL_EXPANDED: 'market_constituents_pl_expanded',
    INDEX_CONSTITUENTS_PL_PINNED: 'market_constituents_pl_pinned',
    INDEX_GAINERS_LOSERS_EXPANDED: 'market_gainers_losers_expanded',
    INDEX_GAINERS_LOSERS_ACTIVE_TAB: 'market_gainers_losers_active_tab',
    PLAYBACK_SPEED: 'oracle_play_speed',
}

// ============================================================================
// 7. INDEX CONSTITUENTS & EXCHANGE RESOLUTION (Centralized)
// ============================================================================
export const TRADINGVIEW_SCANNER_URL = '/api/constituents-scan';
export const DIRECT_TRADINGVIEW_SCANNER_URL = 'https://scanner.tradingview.com/india/scan';

export const INDEX_EXCHANGE_MAP: Record<IndexSymbol, 'NSE' | 'BSE'> = {

    [IndexSymbol.NIFTY]: 'NSE',
    [IndexSymbol.BANKNIFTY]: 'NSE',
    [IndexSymbol.SENSEX]: 'BSE',
};

export const INDEX_CONSTITUENT_SYMBOLS: Record<IndexSymbol, string[]> = {
    [IndexSymbol.NIFTY]: [
        'COALINDIA', 'ICICIBANK', 'HDFCBANK', 'BHARTIARTL', 'INFY',
        'ETERNAL', 'TCS', 'RELIANCE', 'KOTAKBANK', 'TATASTEEL',
        'M&M', 'ADANIPORTS', 'TECHM', 'SBIN', 'BAJFINANCE',
        'HCLTECH', 'AXISBANK', 'ITC', 'MARUTI', 'LT',
        'BEL', 'BAJAJ-AUTO', 'TITAN', 'APOLLOHOSP', 'ULTRACEMCO',
        'EICHERMOT', 'WIPRO', 'NTPC', 'SHRIRAMFIN', 'GRASIM',
        'SUNPHARMA', 'HINDUNILVR', 'JSWSTEEL', 'TMPV', 'MAXHEALTH',
        'SBILIFE', 'ONGC', 'HINDALCO', 'JIOFIN', 'ADANIENT',
        'POWERGRID', 'TRENT', 'INDIGO', 'ASIANPAINT', 'HDFCLIFE',
        'TATACONSUM', 'BAJAJFINSV', 'DRREDDY', 'NESTLEIND', 'CIPLA'
    ],

    [IndexSymbol.BANKNIFTY]: [
        'ICICIBANK', 'HDFCBANK', 'KOTAKBANK', 'SBIN', 'AXISBANK',
        'CANBK', 'YESBANK', 'IDFCFIRSTB', 'FEDERALBNK', 'INDUSINDBK',
        'PNB', 'AUBANK', 'BANKBARODA', 'UNIONBANK'
    ],

    [IndexSymbol.SENSEX]: [
        'TCS', 'TITAN', 'TECHM', 'INFY', 'HCLTECH',
        'TRENT', 'SUNPHARMA', 'AXISBANK', 'HDFCBANK', 'LT',
        'INDIGO', 'BHARTIARTL', 'ETERNAL', 'HINDUNILVR', 'NTPC',
        'POWERGRID', 'BEL', 'SBIN', 'KOTAKBANK', 'BAJFINANCE',
        'ADANIPORTS', 'TATASTEEL', 'ITC', 'RELIANCE', 'BAJAJFINSV',
        'M&M', 'ICICIBANK', 'ASIANPAINT', 'MARUTI', 'ULTRACEMCO'
    ]

};

export const INDEX_CONSTITUENT_WEIGHTS: Record<IndexSymbol, Record<string, number>> = {
    [IndexSymbol.NIFTY]: {
        'HDFCBANK': 11.5, 'RELIANCE': 9.2, 'ICICIBANK': 7.8, 'INFY': 5.3, 'ITC': 4.1,
        'TCS': 4.1, 'BHARTIARTL': 4.0, 'LT': 3.8, 'AXISBANK': 3.3, 'KOTAKBANK': 3.0,
        'SBIN': 2.9, 'M&M': 2.4, 'MARUTI': 1.8, 'SUNPHARMA': 1.7, 'TITAN': 1.6,
        'BAJFINANCE': 1.6, 'TATAMOTORS': 1.6, 'NTPC': 1.4, 'ULTRACEMCO': 1.3, 'POWERGRID': 1.2,
        'TATASTEEL': 1.2, 'HINDUNILVR': 1.2, 'TRENT': 1.2, 'ADANIENT': 1.1, 'BAJAJFINSV': 1.0,
        'JSWSTEEL': 1.0, 'COALINDIA': 0.9, 'ONGC': 0.9, 'GRASIM': 0.9, 'HCLTECH': 0.8,
        'ADANIPORTS': 0.8, 'TECHM': 0.8, 'NESTLEIND': 0.7, 'CIPLA': 0.7, 'SBILIFE': 0.7,
        'SHREECEM': 0.6, 'ETERNAL': 0.6, 'BEL': 0.6, 'DRREDDY': 0.6, 'INDUSINDBK': 0.6,
        'EICHERMOT': 0.6, 'TATACONSUM': 0.6, 'MAXHEALTH': 0.5, 'APOLLOHOSP': 0.5, 'ASIANPAINT': 0.5,
        'HDFCLIFE': 0.5, 'DIVISLAB': 0.5, 'BPCL': 0.5, 'BRITANNIA': 0.5, 'INDIGO': 0.5
    },
    [IndexSymbol.BANKNIFTY]: {
        'HDFCBANK': 28.5, 'ICICIBANK': 23.0, 'SBIN': 10.5, 'AXISBANK': 9.5, 'KOTAKBANK': 9.0,
        'INDUSINDBK': 5.5, 'BANKBARODA': 3.0, 'PNB': 2.5, 'FEDERALBNK': 2.0, 'AUBANK': 2.0,
        'CANBK': 1.5, 'IDFCFIRSTB': 1.5, 'UNIONBANK': 1.0, 'YESBANK': 0.5
    },
    [IndexSymbol.SENSEX]: {
        'HDFCBANK': 13.5, 'RELIANCE': 11.0, 'ICICIBANK': 9.2, 'INFY': 6.5, 'ITC': 5.0,
        'TCS': 4.9, 'BHARTIARTL': 4.8, 'LT': 4.5, 'AXISBANK': 3.9, 'KOTAKBANK': 3.6,
        'SBIN': 3.5, 'M&M': 2.8, 'MARUTI': 2.2, 'SUNPHARMA': 2.0, 'TITAN': 1.9,
        'BAJFINANCE': 1.9, 'NTPC': 1.7, 'ULTRACEMCO': 1.6, 'POWERGRID': 1.5, 'TATASTEEL': 1.5,
        'HINDUNILVR': 1.5, 'TRENT': 1.4, 'BAJAJFINSV': 1.2, 'HCLTECH': 1.0, 'ADANIPORTS': 1.0,
        'TECHM': 1.0, 'BEL': 0.8, 'ETERNAL': 0.7, 'ASIANPAINT': 0.6, 'INDIGO': 0.6
    }
};

export function getIndexExchange(symbol: IndexSymbol | string): 'NSE' | 'BSE' {
    const canon = String(getCanonicalSymbol(symbol)) as IndexSymbol;
    return INDEX_EXCHANGE_MAP[canon] || 'NSE';
}

/**
 * Formats TradingView tickers for constituent stocks.
 * Note: All stocks are queried from NSE to ensure real-time 5-second updates (avoiding BSE 15-min delay).
 */
export function formatTvTicker(rawSymbol: string, _exchange?: 'NSE' | 'BSE'): string {
    let s = rawSymbol;
    if (s === 'BAJAJ-AUTO') s = 'BAJAJ_AUTO';
    if (s === 'TATAMOTORS') s = 'TMPV';
    return `NSE:${s}`;
}

export function getFormattedTickers(symbol: IndexSymbol | string): string[] {
    const canon = String(getCanonicalSymbol(symbol)) as IndexSymbol;
    const rawList = INDEX_CONSTITUENT_SYMBOLS[canon] || [];
    return rawList.map(ticker => formatTvTicker(ticker, 'NSE'));
}

export const INDEX_SPOT_TICKERS: Record<IndexSymbol, string> = {
    [IndexSymbol.NIFTY]: 'NSE:NIFTY',
    [IndexSymbol.BANKNIFTY]: 'NSE:BANKNIFTY',
    [IndexSymbol.SENSEX]: 'BSE:SENSEX',
};

export function getAllIndexFormattedTickers(): string[] {
    const set = new Set<string>();
    (Object.keys(INDEX_CONSTITUENT_SYMBOLS) as IndexSymbol[]).forEach(sym => {
        getFormattedTickers(sym).forEach(t => set.add(t));
        if (INDEX_SPOT_TICKERS[sym]) {
            set.add(INDEX_SPOT_TICKERS[sym]);
        }
    });
    return Array.from(set);
}

export const MONEYCONTROL_INDEX_IDS: Record<IndexSymbol, number> = {

    [IndexSymbol.NIFTY]: 9,
    [IndexSymbol.BANKNIFTY]: 23,
    [IndexSymbol.SENSEX]: 4,
};

export const MONEYCONTROL_MARKETMAP_URL = '/api/mc-marketmap';
export const DIRECT_MONEYCONTROL_MARKETMAP_URL = 'https://appfeeds.moneycontrol.com/jsonapi/market/marketmap&format=json&type=0';

/**
 * Normalizes Moneycontrol shortnames / IDs to canonical constituent symbols.
 */
export function normalizeMoneycontrolSymbol(item: { shortname?: string; id?: string; sc_did?: string }): string {
    const name = (item.shortname || '').toUpperCase().trim();
    const id = (item.id || '').toUpperCase().trim();
    const scDid = (item.sc_did || '').toUpperCase().trim();

    // Specific multi-word / compound names first
    if (name.includes('TECH MAHINDRA') || name.includes('TECH M') || id === 'TM4') return 'TECHM';
    if (name.includes('KOTAK')) return 'KOTAKBANK';
    if (name.includes('M&M') || name === 'MAHINDRA & MAHINDRA' || id === 'MM') return 'M&M';
    if (name.includes('HDFC BANK')) return 'HDFCBANK';
    if (name.includes('ICICI BANK')) return 'ICICIBANK';
    if (name.includes('AXIS BANK')) return 'AXISBANK';
    if (name.includes('SBI LIFE')) return 'SBILIFE';
    if (name.includes('SBI') || name === 'STATE BANK OF INDIA') return 'SBIN';
    if (name.includes('INDUSIND')) return 'INDUSINDBK';
    if (name.includes('FEDERAL BANK')) return 'FEDERALBNK';
    if (name.includes('IDFC FIRST')) return 'IDFCFIRSTB';
    if (name.includes('CANARA BANK') || name === 'CANARA BANK') return 'CANBK';
    if (name.includes('UNION BANK') || name === 'UNION BANK') return 'UNIONBANK';
    if (name.includes('YES BANK')) return 'YESBANK';
    if (name.includes('BANK OF BARODA')) return 'BANKBARODA';
    if (name.includes('AU SMALL')) return 'AUBANK';
    if (name.includes('BAJAJ AUTO')) return 'BAJAJ-AUTO';
    if (name.includes('BAJAJ FINANCE')) return 'BAJFINANCE';
    if (name.includes('BAJAJ FINSERV')) return 'BAJAJFINSV';
    if (name.includes('BHARAT ELEC') || name.includes('BEL') || id === 'BE03') return 'BEL';
    if (name.includes('BHARTI AIRTEL')) return 'BHARTIARTL';
    if (name.includes('HCL TECH')) return 'HCLTECH';
    if (name.includes('HUL') || name.includes('HINDUSTAN UNILEVER')) return 'HINDUNILVR';
    if (name.includes('HDFC LIFE')) return 'HDFCLIFE';
    if (name.includes('INTERGLOBE') || name.includes('INDIGO')) return 'INDIGO';
    if (name.includes('LARSEN') || name === 'L&T' || name === 'LT') return 'LT';
    if (name.includes('MARUTI')) return 'MARUTI';
    if (name.includes('MAX HEALTHCARE') || name.includes('MAX HEALTH') || id === 'MHI') return 'MAXHEALTH';
    if (name.includes('NESTLE')) return 'NESTLEIND';
    if (name.includes('POWER GRID') || name.includes('POWERGRID') || id === 'PGC') return 'POWERGRID';
    if (name.includes('SUN PHARMA')) return 'SUNPHARMA';
    if (name.includes('TATA CONS') || name.includes('TATA CONSUMER')) return 'TATACONSUM';
    if (name.includes('TATA STEEL')) return 'TATASTEEL';
    if (name === 'TCS' || name.includes('TATA CONSULTANCY')) return 'TCS';
    if (name.includes('TITAN')) return 'TITAN';
    if (name.includes('TMPV') || name.includes('TATA MOTORS') || id === 'TEL' || scDid === 'TM03') return 'TMPV';
    if (name.includes('ULTRATECH') || name.includes('ULTRA TECH')) return 'ULTRACEMCO';
    if (name.includes('APOLLO HOSPITAL')) return 'APOLLOHOSP';
    if (name.includes('ASIAN PAINTS') || name.includes('ASIAN PAINT')) return 'ASIANPAINT';
    if (name.includes('ADANI ENTERPRIS')) return 'ADANIENT';
    if (name.includes('ADANI PORTS') || name.includes('ADANI PORT')) return 'ADANIPORTS';
    if (name.includes('COAL INDIA')) return 'COALINDIA';
    if (name.includes('DR REDDY')) return 'DRREDDY';
    if (name.includes('EICHER MOTORS')) return 'EICHERMOT';
    if (name.includes('JSW STEEL')) return 'JSWSTEEL';
    if (name.includes('SHREE CEMENT')) return 'SHREECEM';
    if (name.includes('ETERNAL')) return 'ETERNAL';
    if (name.includes('DIVI')) return 'DIVISLAB';
    if (name.includes('BHARAT PETRO') || name.includes('BPCL')) return 'BPCL';
    if (name.includes('BRITANNIA')) return 'BRITANNIA';
    if (name.includes('CIPLA')) return 'CIPLA';
    if (name.includes('GRASIM')) return 'GRASIM';
    if (name.includes('INFOSYS') || name === 'INFY') return 'INFY';
    if (name === 'ITC') return 'ITC';
    if (name.includes('NTPC')) return 'NTPC';
    if (name.includes('ONGC')) return 'ONGC';
    if (name.includes('RELIANCE')) return 'RELIANCE';
    if (name.includes('TRENT')) return 'TRENT';
    if (name.includes('WIPRO')) return 'WIPRO';
    if (name.includes('HINDALCO')) return 'HINDALCO';
    if (name.includes('JIO FINANCIAL') || id === 'JFS') return 'JIOFIN';
    if (name.includes('PNB') || name.includes('PUNJAB NATIONAL')) return 'PNB';
    if (name.includes('SHRIRAM FINANCE') || id === 'STF') return 'SHRIRAMFIN';

    return name;
}

// ============================================================================
// 8. TRADING PSYCHOLOGY & DISCIPLINE CONFIGURATION
// ============================================================================
export interface PsychologyAvoidItem {
    id: string;
    label: string;
    shortLabel?: string;
    icon: string;
    category: 'Risk Defense' | 'Emotional Mastery' | 'Execution';
    description: string;
    antidote: string;
    badge?: string;
}

export const TRADING_PSYCHOLOGY_CONFIG = {
    MANTRA: 'I am Trader Not Gambler – Just Follow the Rules',
    MANTRA_TITLE: 'I AM TRADER, NOT GAMBLER',
    MANTRA_PREFIX: 'I AM',
    MANTRA_TRADER: 'TRADER',
    MANTRA_MIDDLE: ', NOT',
    MANTRA_GAMBLER: 'GAMBLER',
    MANTRA_SUBTITLE: 'JUST FOLLOW THE RULES',
    TAGLINE: 'Professional Execution • Zero Emotional Tilt',
    QUOTATIONS: [
        'Human brain is made for survival, not for trading',
        'Clarity makes money; confidence just feeds ego',
    ] as const,
    QUOTE_SURVIVAL: 'Human brain is made for survival, not for trading',
    QUOTE_CLARITY: 'Clarity makes money; confidence just feeds ego',
    STATUS_BADGE: 'Discipline Shield Active',
    AVOID_LABEL: 'ZERO TOLERANCE: AVOID',
    AVOID_SHORT_LABEL: 'AVOID',
    CATEGORIES: [
        { key: 'Risk Defense', label: 'Capital Defense', icon: '🛡️', color: 'rose' },
        { key: 'Emotional Mastery', label: 'Emotional Mastery', icon: '🧠', color: 'amber' },
        { key: 'Execution', label: 'Execution Edge', icon: '🎯', color: 'indigo' },
    ],
    AFFIRMATION: 'Discipline Equals Freedom • Cut Losers Quickly • Let Winners Run',
    AVOID_ITEMS: [
        {
            id: 'greedy',
            label: 'Greed',
            shortLabel: 'Greed',
            icon: '💰',
            category: 'Emotional Mastery' as const,
            description: 'Holding past target expecting infinite profits until winners turn to losses.',
            antidote: 'Book profits at predetermined targets; trail SL to protect capital.',
            badge: 'Profit Rule'
        },
        {
            id: 'regret',
            label: 'Regret',
            shortLabel: 'Regret',
            icon: '💭',
            category: 'Emotional Mastery' as const,
            description: 'Crying over stopped-out trades or missed rallies, clouding the next trade.',
            antidote: 'Stop losses are the cost of doing business; endless opportunities await.',
            badge: 'Mindset'
        },
        {
            id: 'revenge',
            label: 'Revenge Trading',
            shortLabel: 'Revenge',
            icon: '⚔️',
            category: 'Emotional Mastery' as const,
            description: 'Entering oversized trades immediately after a loss to make money back.',
            antidote: 'Step away from screens after a loss; never fight the market.',
            badge: 'Stop Tilt'
        },
        {
            id: 'overtrading',
            label: 'Over Trading',
            shortLabel: 'Overtrade',
            icon: '🔄',
            category: 'Risk Defense' as const,
            description: 'Trading out of boredom or chasing low-probability non-setup moves.',
            antidote: 'Limit daily trades; high frequency guarantees fee drag & capital loss.',
            badge: 'Capital Guard'
        },
        {
            id: 'emotions',
            label: 'Emotions',
            shortLabel: 'Emotions',
            icon: '🎭',
            category: 'Emotional Mastery' as const,
            description: 'Trading on fear of loss, euphoria after a win, or gut feeling.',
            antidote: 'Execute like a machine; follow data, levels, and predefined plans.',
            badge: 'Cold Mind'
        },
        {
            id: 'fomo',
            label: 'FOMO',
            shortLabel: 'FOMO',
            icon: '⚡',
            category: 'Execution' as const,
            description: 'Chasing extended candles at resistance or panic buying at tops.',
            antidote: 'If you missed the entry, wait for the pullback or the next setup.',
            badge: 'Never Chase'
        },
        {
            id: 'hope',
            label: 'Hope Trading',
            shortLabel: 'Hope',
            icon: '🕯️',
            category: 'Execution' as const,
            description: 'Holding losing trades praying that the price turns back in your favor.',
            antidote: 'Hope is not a trading strategy; exit immediately when invalid.',
            badge: 'Cut Loser'
        },
        {
            id: 'averaging',
            label: 'Averaging Losses',
            shortLabel: 'Avg Loss',
            icon: '📉',
            category: 'Risk Defense' as const,
            description: 'Adding more contracts to a losing trade to bring down average price.',
            antidote: 'Never add to a loser; average winners only when trend is confirmed.',
            badge: 'Fatal Trap'
        },
        {
            id: 'impatience',
            label: 'Impatience',
            shortLabel: 'Impatience',
            icon: '⏳',
            category: 'Execution' as const,
            description: 'Jumping into positions before the setup confirms or candle closes.',
            antidote: 'Cash is an active position; patient traders take the market money.',
            badge: 'Patience Pays'
        },
    ] as PsychologyAvoidItem[],
} as const;

// ============================================================================
// 12. FUTURES FEED CONFIGURATION (Centralized Primary & Backup Feeds)
// ============================================================================
export const FUTURES_FEED_CONFIG = {
    PROXY_URL: '/api/future-quote',
    REFRESH_MS: 2000,
    PRIMARY: {
        [IndexSymbol.NIFTY]: 'https://groww.in/futures/nifty',
        [IndexSymbol.BANKNIFTY]: 'https://groww.in/futures/nifty-bank',
        [IndexSymbol.SENSEX]: 'https://groww.in/futures/sp-bse-sensex',
    } as Record<IndexSymbol, string>,
    BACKUP: {
        [IndexSymbol.NIFTY]: 'https://service.upstox.com/market-data-api/v2/open/smartlist/getAssetsTopOptionsAndFutures?underlierIk=NSE_INDEX%7CNifty+50',
        [IndexSymbol.BANKNIFTY]: 'https://api.upstox.com/v2/historical-candle',
        [IndexSymbol.SENSEX]: 'https://api.upstox.com/v2/historical-candle',
    } as Record<IndexSymbol, string>,
    UPSTOX_FUT_TOKENS: {
        [IndexSymbol.NIFTY]: 'NSE_FO|68407',
        [IndexSymbol.BANKNIFTY]: 'NSE_FO|68390',
        [IndexSymbol.SENSEX]: 'BSE_FO|844615',
    } as Record<IndexSymbol, string>,
} as const;

